import React, { useState } from "react";
import { Download, Eye, Search } from "lucide-react";
import { toast } from "sonner";
import { docsApi, docsErr } from "@/lib/docsApi";
import { btnGold, Chip, Empty, ErrorBox, field, Loading, useLoad } from "@/components/nds/windows/finance/parts";

const open = (path) => docsApi.openPdf(path).catch((e) => toast.error(docsErr(e)));

function Row({ x, path, who }) {
  const signed = x.status === "signed";
  return (
    <li data-testid={`pl-row-${x.id}`} className="px-4 py-2.5 flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-[180px]">
        <div className="text-nex-silver">{x.title} <span className="text-xs text-nex-muted">v{x.version}</span></div>
        <div className="text-xs text-nex-muted">{who}{signed ? ` · signed ${x.signed_at_local}` : " · awaiting signature"}</div>
      </div>
      {x.via && <Chip tone={x.via === "referred" ? "purple" : "blue"}>{{ referred: "Your referral", assigned: "Assigned", program: "Program-wide access" }[x.via]}</Chip>}
      {signed ? <button type="button" onClick={() => open(path)} data-testid={`pl-view-${x.id}`} className={`${btnGold} inline-flex items-center gap-1`}><Eye className="h-3 w-3" /> View · <Download className="h-3 w-3" /></button>
        : <Chip tone="yellow" data-testid={`pl-waiting-${x.id}`}>Awaiting signature</Chip>}
    </li>
  );
}

function Section({ title, testid, children, empty }) {
  return (
    <section data-testid={testid} className="space-y-2">
      <h3 className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-gold">{title}</h3>
      {React.Children.count(children) ? <ul className="divide-y divide-white/5 border border-white/10 rounded-sm">{children}</ul> : <Empty testid={`${testid}-empty`}>{empty}</Empty>}
    </section>
  );
}

/** Partner document library: what the partner signed, plus the documents of residents assigned to or referred by them. */
export function PartnerLibrary() {
  const [q, setQ] = useState("");
  const [mine, loadMine] = useLoad(() => docsApi.get("/me"), []);
  const [res, loadRes] = useLoad(() => docsApi.get("/partner-desk/residents"), []);
  if ((mine.loading && !mine.data) || (res.loading && !res.data)) return <Loading testid="pl-loading" />;
  if (mine.error || res.error) return <ErrorBox testid="pl-error" error={mine.error || res.error} onRetry={() => { loadMine(); loadRes(); }} />;
  const t = q.trim().toLowerCase();
  const own = mine.data.documents.filter((x) => x.status === "signed" && (!t || x.title.toLowerCase().includes(t)));
  const docs = res.data.documents.filter((x) => !t || `${x.title} ${x.person_name}`.toLowerCase().includes(t));
  return (
    <div className="space-y-5" data-testid="partner-library">
      <div className="relative"><Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-nex-muted" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by resident or document" data-testid="pl-search" className={`${field} w-full pl-8`} /></div>
      <Section title={`Documents you signed · ${own.length}`} testid="pl-mine" empty="You haven't signed any documents yet.">
        {own.map((x) => <Row key={x.id} x={x} who="You" path={`/me/${x.id}/file`} />)}
      </Section>
      <Section title={`Your residents' documents · ${docs.filter((x) => x.status === "signed").length} signed · ${docs.filter((x) => x.status === "pending").length} waiting`} testid="pl-residents" empty="No documents yet for residents assigned to you or referred by you.">
        {docs.map((x) => <Row key={x.id} x={x} who={x.person_name} path={`/partner-desk/residents/${x.id}/file`} />)}
      </Section>
    </div>
  );
}
